class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.boundClose = this.close.bind(this);
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
  }

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay) return;

    overlay.removeEventListener('click', this.boundClose);
    overlay.addEventListener('click', this.boundClose);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || cartLink.dataset.cartDrawerAccessibilityBound === 'true') return;

    cartLink.dataset.cartDrawerAccessibilityBound = 'true';
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, options = {}) {
    const openDrawer = options.openDrawer !== false;
    const drawerInner = this.querySelector('.drawer__inner');

    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionHtml = parsedState.sections?.[section.id];
      if (!sectionHtml) return;

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
    });

    setTimeout(() => {
      this.bindOverlay();
      this.setHeaderCartIconAccessibility();

      if (openDrawer) this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    const section = parsedDocument.querySelector(selector);

    return section ? section.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}

class EcomhardGiftController {
  constructor() {
    this.syncing = false;
    this.scheduled = false;
    this.timer = null;
    this.observer = null;

    this.boot = this.boot.bind(this);
    this.schedule = this.schedule.bind(this);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.boot, { once: true });
    } else {
      this.boot();
    }
  }

  boot() {
    if (!this.getConfig()) return;

    this.bindCartEvents();
    this.observeCartChanges();
    this.schedule(80);
  }

  bindCartEvents() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.cartUpdate) return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      this.schedule(140);
    });
  }

  observeCartChanges() {
    if (this.observer) return;

    const targets = [
      document.querySelector('cart-drawer'),
      document.querySelector('cart-items'),
    ].filter(Boolean);

    if (!targets.length || typeof MutationObserver === 'undefined') return;

    this.observer = new MutationObserver(() => {
      this.schedule(180);
    });

    targets.forEach((target) => {
      this.observer.observe(target, {
        childList: true,
        subtree: true,
      });
    });
  }

  getConfig() {
    const root = document.querySelector('[data-ecomhard-gift-enabled="true"]');
    if (!root) return null;

    const variantId = Number(root.dataset.ecomhardGiftVariantId);
    const threshold = Number(root.dataset.ecomhardGiftThreshold);

    if (!Number.isFinite(variantId) || variantId <= 0 || !Number.isFinite(threshold) || threshold <= 0) {
      return null;
    }

    return { variantId, threshold };
  }

  schedule(delay = 100) {
    if (!this.getConfig()) return;

    this.scheduled = true;
    window.clearTimeout(this.timer);

    this.timer = window.setTimeout(() => {
      this.timer = null;
      this.run();
    }, delay);
  }

  async run() {
    if (this.syncing) {
      this.scheduled = true;
      return;
    }

    this.scheduled = false;
    this.syncing = true;

    try {
      const config = this.getConfig();
      if (!config) return;

      const cart = await this.fetchCart();
      const giftLines = cart.items.filter((item) => this.isGiftLine(item));
      const eligibleSubtotal = cart.items.reduce((total, item) => {
        return this.isGiftLine(item) ? total : total + item.final_line_price;
      }, 0);
      const qualifies = eligibleSubtotal >= config.threshold;

      let state = null;

      if (!qualifies && giftLines.length) {
        state = await this.removeGiftLines(giftLines);
      }

      if (qualifies && !this.hasValidGiftLine(giftLines, config.variantId)) {
        if (giftLines.length) {
          state = await this.removeGiftLines(giftLines);
        }

        state = await this.addGift(config.variantId);
      }

      if (state?.sections) {
        this.renderCartState(state);
      }
    } catch (error) {
      console.error('[ECOMHARD] Não foi possível sincronizar o brinde automático.', error);
    } finally {
      this.syncing = false;

      if (this.scheduled) {
        this.scheduled = false;
        this.schedule(80);
      }
    }
  }

  isGiftLine(item) {
    return String(item?.properties?._ecomhard_brinde || '') === 'true';
  }

  hasValidGiftLine(giftLines, variantId) {
    return (
      giftLines.length === 1 &&
      Number(giftLines[0].variant_id) === Number(variantId) &&
      Number(giftLines[0].quantity) === 1
    );
  }

  async fetchCart() {
    const response = await fetch(`${routes.cart_url}.js`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Cart request failed with status ${response.status}`);
    }

    return response.json();
  }

  getSectionsToRequest() {
    const sections = [];
    const mainCartItems = document.getElementById('main-cart-items');

    if (document.querySelector('cart-drawer')) sections.push('cart-drawer');
    if (mainCartItems?.dataset.id) sections.push(mainCartItems.dataset.id);
    if (document.getElementById('main-cart-footer')) sections.push('main-cart-footer');
    if (document.getElementById('cart-icon-bubble')) sections.push('cart-icon-bubble');

    return [...new Set(sections)];
  }

  async removeGiftLines(giftLines) {
    const updates = {};

    giftLines.forEach((item) => {
      updates[item.key] = 0;
    });

    return this.requestCartUpdate({ updates });
  }

  async addGift(variantId) {
    const sections = this.getSectionsToRequest();
    const response = await fetch(routes.cart_add_url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: {
              _ecomhard_brinde: 'true',
            },
          },
        ],
        sections,
        sections_url: window.location.pathname,
      }),
    });

    if (!response.ok) {
      throw new Error(`Gift add request failed with status ${response.status}`);
    }

    return response.json();
  }

  async requestCartUpdate(payload) {
    const sections = this.getSectionsToRequest();
    const response = await fetch(routes.cart_update_url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...payload,
        sections,
        sections_url: window.location.pathname,
      }),
    });

    if (!response.ok) {
      throw new Error(`Cart update request failed with status ${response.status}`);
    }

    return response.json();
  }

  renderCartState(state) {
    if (typeof state.item_count === 'number') {
      const isEmpty = state.item_count === 0;
      document.querySelector('cart-drawer')?.classList.toggle('is-empty', isEmpty);
      document.querySelector('cart-items')?.classList.toggle('is-empty', isEmpty);
      document.getElementById('main-cart-footer')?.classList.toggle('is-empty', isEmpty);
    }

    const drawer = document.querySelector('cart-drawer');
    if (drawer && state.sections?.['cart-drawer']) {
      drawer.renderContents(state, { openDrawer: false });
    } else {
      this.replaceSectionContent(state, 'cart-icon-bubble', '.shopify-section');
    }

    const mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems?.dataset.id) {
      this.replaceSectionContent(
        state,
        mainCartItems.dataset.id,
        '.js-contents',
        mainCartItems.querySelector('.js-contents')
      );
    }

    this.replaceSectionContent(
      state,
      'main-cart-footer',
      '.js-contents',
      document.querySelector('#main-cart-footer .js-contents')
    );
  }

  replaceSectionContent(state, sectionId, selector, target = null) {
    const sectionHtml = state.sections?.[sectionId];
    if (!sectionHtml) return;

    const source = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector(selector);
    const element = target || document.getElementById(sectionId);

    if (source && element) {
      element.innerHTML = source.innerHTML;
    }
  }
}

if (!window.EcomhardGiftSync) {
  window.EcomhardGiftSync = new EcomhardGiftController();
}
